import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Archive, ArchiveRestore, Copy, Edit,
  Film, FolderOpen, Plus, Zap, Eye, MousePointerClick, TrendingUp,
} from "lucide-react";
import {
  useAllCreatives, useCreativeStats, usePatchCreativeStatus,
  useCampaigns, useCampaignCreatives, useReplaceCampaignCreatives,
  type AllCreativeItem, type CampaignSummary, type CreativeStatEntry,
} from "@/shared/api/wb";
import { Button, Skeleton } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import { fmtNum } from "@/shared/lib/format";
import { TelegramPost } from "./CampaignWizard/TelegramPost";
import { CreativeEditor, type VariantMedia } from "./CampaignWizard/CreativeEditor";
import type { CreativeVariant } from "@/shared/store/draft";

type Tab = "active" | "drafts" | "archive";

const TAB_LABELS: Record<Tab, string> = {
  active:  "Активные",
  drafts:  "Черновики",
  archive: "Архив",
};

function variantStatus(item: AllCreativeItem): "active" | "draft" | "archived" {
  return item.variant.status;
}

export function CreativesPage() {
  const [tab, setTab] = useState<Tab>("active");
  const [newOpen, setNewOpen] = useState(false);
  const { data, isLoading } = useAllCreatives();
  const { data: stats } = useCreativeStats();

  const all = data ?? [];
  const grouped: Record<Tab, AllCreativeItem[]> = {
    active:  all.filter((i) => variantStatus(i) === "active"),
    drafts:  all.filter((i) => variantStatus(i) === "draft"),
    archive: all.filter((i) => variantStatus(i) === "archived"),
  };
  const displayed = grouped[tab];

  if (isLoading) return <CreativesSkeleton />;

  return (
    <div className="p-7 pb-16">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-1">
            Библиотека рекламных материалов
          </div>
          <h1 className="font-display font-extrabold tracking-tight text-[28px] leading-tight">
            Мои креативы
          </h1>
        </div>
        <Button onClick={() => setNewOpen(true)}>
          <Plus size={15} /> Новый креатив
        </Button>
      </div>

      <div className="inline-flex p-1 rounded-pill bg-ink-6 gap-0.5 mb-6">
        {(["active", "drafts", "archive"] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-pill text-[13px] font-display font-semibold transition-colors",
              tab === t ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:text-ink",
            )}>
            {TAB_LABELS[t]}
            {grouped[t].length > 0 && (
              <span className="font-mono text-[11px] opacity-60">{grouped[t].length}</span>
            )}
          </button>
        ))}
      </div>

      {displayed.length === 0 ? (
        <EmptyTab tab={tab} />
      ) : (
        <div className="grid grid-cols-3 gap-5">
          {displayed.map((item) => (
            <CreativeCard
              key={`${item.campaign_id}-${item.variant.id}`}
              item={item}
              tab={tab}
              stat={stats?.[item.variant.id]}
            />
          ))}
        </div>
      )}

      {tab === "active" && displayed.length > 0 && stats && (
        <BestPerformerBanner items={displayed} stats={stats} />
      )}

      {newOpen && <NewCreativeModal onClose={() => setNewOpen(false)} />}
    </div>
  );
}

function CreativeCard({ item, tab, stat }: {
  item: AllCreativeItem;
  tab: Tab;
  stat?: CreativeStatEntry;
}) {
  const navigate = useNavigate();
  const patch = usePatchCreativeStatus(item.campaign_id);
  const TypeIcon = item.campaign_type === "seeding" ? Copy : Edit;
  const typeLabel = item.campaign_type === "seeding" ? "Посев" : "Натив";
  const isArchived = tab === "archive";

  return (
    <div className={cn(
      "bg-white rounded-lg border flex flex-col overflow-hidden transition-colors hover:border-ink/30",
      isArchived ? "border-ink-12 opacity-75" : "border-ink-12",
    )}>
      {/* Campaign header */}
      <div className="px-4 pt-3.5 pb-3 border-b border-ink-12/60 flex items-center gap-2 min-w-0">
        <span className={cn(
          "inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-[10.5px] font-display font-semibold flex-shrink-0",
          item.campaign_type === "seeding" ? "bg-lime/20 text-ink" : "bg-pink/20 text-ink",
        )}>
          <TypeIcon size={10} strokeWidth={2.5} /> {typeLabel}
        </span>
        <div className="font-display font-bold text-[13px] truncate flex-1 min-w-0">
          {item.campaign_product}
        </div>
      </div>

      {/* Variant name */}
      <div className="px-4 pt-3 pb-2">
        <div className="font-display font-semibold text-[13px] text-ink-60">{item.variant.name}</div>
      </div>

      {/* Creative preview */}
      <div className="px-4 pb-3 flex-1 overflow-hidden">
        <div className="max-h-[280px] overflow-hidden rounded-md pointer-events-none">
          <TelegramPost
            p={{
              name: item.variant.name,
              text: item.variant.text,
              media: item.variant.media,
              link: item.variant.link,
            }}
            compact
          />
        </div>
      </div>

      {/* Stats (active tab only, if data exists) */}
      {tab === "active" && stat && stat.views > 0 && (
        <div className="mx-4 mb-3 grid grid-cols-3 gap-1.5">
          <StatChip icon={<Eye size={11} />} label="Просмотры" value={fmtNum(stat.views)} />
          <StatChip icon={<MousePointerClick size={11} />} label="Клики" value={fmtNum(stat.clicks)} />
          <StatChip icon={<TrendingUp size={11} />} label="CPV" value={`${stat.cpv} ₽`} />
        </div>
      )}

      {/* Actions */}
      <div className="px-4 pb-4 pt-1 border-t border-ink-12/60 flex gap-2 flex-wrap">
        <button
          onClick={() => navigate(`/campaign/${item.campaign_id}`)}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors"
        >
          В кампании <ArrowRight size={12} />
        </button>

        {tab === "drafts" && (
          <button
            onClick={() => patch.mutate({ variantId: item.variant.id, status: "active" })}
            disabled={patch.isPending}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[12px] font-display font-semibold hover:opacity-90 transition-opacity"
          >
            <Zap size={12} /> Активировать
          </button>
        )}

        {tab === "archive" ? (
          <button
            onClick={() => patch.mutate({ variantId: item.variant.id, status: "active" })}
            disabled={patch.isPending}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors"
          >
            <ArchiveRestore size={12} /> Восстановить
          </button>
        ) : (
          <button
            onClick={() => patch.mutate({ variantId: item.variant.id, status: "archived" })}
            disabled={patch.isPending}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border border-ink-12 text-[12px] font-display font-semibold text-ink-60 hover:text-ink hover:border-ink/40 transition-colors"
          >
            <Archive size={12} /> В архив
          </button>
        )}
      </div>
    </div>
  );
}

function StatChip({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5 rounded-md bg-ink-6 px-2 py-1.5">
      <div className="flex items-center gap-1 text-ink-40">{icon}
        <span className="text-[9.5px] font-display font-bold uppercase tracking-[0.05em]">{label}</span>
      </div>
      <div className="font-mono font-bold text-[12px]">{value}</div>
    </div>
  );
}

function BestPerformerBanner({ items, stats }: {
  items: AllCreativeItem[];
  stats: Record<string, CreativeStatEntry>;
}) {
  const withStats = items.filter((i) => stats[i.variant.id]?.views > 0);
  if (withStats.length < 2) return null;

  const best = withStats.reduce((a, b) =>
    (stats[a.variant.id]?.cpv ?? Infinity) < (stats[b.variant.id]?.cpv ?? Infinity) ? a : b
  );
  const s = stats[best.variant.id];
  const worstCpv = Math.max(...withStats.map((i) => stats[i.variant.id]?.cpv ?? 0));
  const pctBetter = worstCpv > 0 ? Math.round((1 - s.cpv / worstCpv) * 100) : 0;

  return (
    <div className="mt-6 flex items-center gap-4 px-5 py-4 rounded-lg bg-lime/15 border border-lime/30">
      <div className="w-9 h-9 rounded-md bg-lime grid place-items-center flex-shrink-0">
        <TrendingUp size={18} className="text-[var(--fg-on-accent)]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-[14px]">Лучший креатив: «{best.variant.name}»</div>
        <div className="text-[12.5px] text-ink-60 mt-0.5">
          {best.campaign_product} · CPV {s.cpv} ₽ · {fmtNum(s.views)} просмотров · CTR {s.ctr}%
          {pctBetter > 0 && <span className="ml-2 text-[var(--wb-lime)] font-semibold">на {pctBetter}% лучше остальных</span>}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.1em] text-ink-40">Конверсии</div>
        <div className="font-display font-extrabold text-[20px] tracking-tight">{fmtNum(s.conv)}</div>
      </div>
    </div>
  );
}

// ─── New creative modal ────────────────────────────────────────────────────────

type NewStep = { kind: "campaign" } | { kind: "edit"; campaign: CampaignSummary };

const EMPTY_VARIANT: Omit<CreativeVariant, "id"> = {
  name: "",
  tone: "Дружеский",
  text: "",
  link: "",
  media: [],
};

function NewCreativeModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<NewStep>({ kind: "campaign" });
  const [variant, setVariant] = useState<Omit<CreativeVariant, "id">>(EMPTY_VARIANT);

  const { data: campaigns } = useCampaigns();
  const campaignId = step.kind === "edit" ? step.campaign.id : null;
  const { data: existing } = useCampaignCreatives(campaignId);
  const replace = useReplaceCampaignCreatives(campaignId ?? "");

  const eligible = (campaigns ?? []).filter((c) => c.status !== "draft");

  async function save() {
    const newVariant: CreativeVariant = {
      ...variant,
      id: `cv${Date.now() % 1000000}`,
      name: variant.name.trim() || `Вариант ${(existing?.variants.length ?? 0) + 1}`,
    };
    const all = [...(existing?.variants ?? []), newVariant];
    await replace.mutateAsync(all);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-xl shadow-lg w-[min(780px,96vw)] max-h-[90vh] flex flex-col fade-up">

        <div className="px-6 pt-5 pb-4 border-b border-ink-12 flex-shrink-0">
          {step.kind === "edit" && (
            <button onClick={() => setStep({ kind: "campaign" })}
              className="flex items-center gap-1 text-[12px] text-ink-40 hover:text-ink mb-2">
              <ArrowLeft size={13} /> Выбрать другую кампанию
            </button>
          )}
          <div className="font-display font-bold text-[20px]">
            {step.kind === "campaign" ? "Новый креатив — выберите кампанию" : `Новый вариант · ${step.campaign.product}`}
          </div>
          {step.kind === "edit" && (
            <div className="text-[12.5px] text-ink-40 mt-0.5">
              Будет добавлен как новый вариант в эту кампанию
            </div>
          )}
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {step.kind === "campaign" && (
            <div className="grid gap-1">
              {eligible.length === 0 && (
                <div className="text-center text-ink-40 py-10 text-[13px]">
                  Нет запущенных кампаний. Сначала создайте РК.
                </div>
              )}
              {eligible.map((c) => (
                <button key={c.id}
                  onClick={() => { setStep({ kind: "edit", campaign: c }); setVariant(EMPTY_VARIANT); }}
                  className="flex items-center justify-between w-full px-4 py-3.5 rounded-lg text-left hover:bg-ink-6 transition-colors border border-transparent hover:border-ink-12">
                  <div>
                    <div className="font-display font-bold text-[14px]">{c.product}</div>
                    <div className="text-[12px] text-ink-40 mt-0.5">
                      {c.type === "seeding" ? "Посев" : "Натив"} · {c.platforms.join(", ")} · {c.placements_count} каналов
                    </div>
                  </div>
                  <ArrowRight size={14} className="text-ink-40 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {step.kind === "edit" && (
            <div className="grid grid-cols-[1fr_280px] gap-6 items-start">
              <div className="grid gap-4">
                <label className="grid gap-1.5">
                  <span className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">Название варианта</span>
                  <input
                    autoFocus
                    value={variant.name}
                    onChange={(e) => setVariant((v) => ({ ...v, name: e.target.value }))}
                    placeholder={`Вариант ${(existing?.variants.length ?? 0) + 1}`}
                    className="h-9 px-3 rounded-md border border-ink-12 text-[14px] font-display outline-none focus:border-ink"
                  />
                </label>
                <div>
                  <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-1.5">Текст поста</div>
                  <CreativeEditor
                    text={variant.text}
                    onText={(t) => setVariant((v) => ({ ...v, text: t }))}
                    media={variant.media as VariantMedia[]}
                    onMedia={(m) => setVariant((v) => ({ ...v, media: m }))}
                  />
                </div>
                <label className="grid gap-1.5">
                  <span className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">Ссылка</span>
                  <input
                    value={variant.link}
                    onChange={(e) => setVariant((v) => ({ ...v, link: e.target.value }))}
                    placeholder="https://example.com"
                    className="h-9 px-3 rounded-md border border-ink-12 text-[14px] font-mono outline-none focus:border-ink"
                  />
                </label>
                <div className="text-[11.5px] text-ink-40">
                  UTM-метки создаются автоматически для каждого канала — смотрите и правьте их в карточке размещения на канбане кампании.
                </div>
              </div>

              <div className="sticky top-0">
                <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40 mb-2">Превью</div>
                <div className="max-h-[480px] overflow-y-auto rounded-md">
                  <TelegramPost
                    p={{ name: variant.name || "Новый вариант", text: variant.text, media: variant.media as VariantMedia[], link: variant.link }}
                    compact
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {step.kind === "edit" && (
          <div className="px-6 py-4 border-t border-ink-12 flex-shrink-0 flex justify-end gap-2">
            <button onClick={onClose}
              className="h-9 px-4 rounded-pill border-2 border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink">
              Отмена
            </button>
            <Button onClick={save} disabled={!variant.text.trim() || replace.isPending}>
              {replace.isPending ? "Сохранение…" : "Сохранить вариант"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyTab({ tab }: { tab: Tab }) {
  const msgs: Record<Tab, { title: string; sub: string }> = {
    active:  { title: "Нет активных креативов", sub: "Запустите кампанию или активируйте черновик — он появится здесь." },
    drafts:  { title: "Нет черновиков", sub: "Создайте новый креатив или запустите кампанию-черновик." },
    archive: { title: "Архив пуст", sub: "Архивированные креативы хранятся здесь и могут быть восстановлены." },
  };
  return (
    <div className="grid place-items-center py-20 text-center">
      <div className="w-14 h-14 rounded-md bg-ink-6 grid place-items-center text-ink-40 mb-4">
        <FolderOpen size={26} />
      </div>
      <div className="font-display font-bold text-[18px] mb-1.5">{msgs[tab].title}</div>
      <div className="text-[13px] text-ink-60 max-w-[380px]">{msgs[tab].sub}</div>
    </div>
  );
}

function CreativesSkeleton() {
  return (
    <div className="p-7 pb-16">
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-36" />
          <Skeleton className="h-8 w-44" />
        </div>
        <Skeleton className="h-4 w-20" />
      </div>
      <Skeleton className="h-9 w-72 rounded-pill mb-6" />
      <div className="grid grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-ink-12 overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-12/60 flex items-center gap-2">
              <Skeleton className="h-5 w-14 rounded-pill" />
              <Skeleton className="h-4 flex-1" />
            </div>
            <div className="px-4 py-3">
              <Skeleton className="h-4 w-24 mb-3" />
              <Skeleton className="h-[180px] rounded-md" />
            </div>
            <div className="px-4 pb-4 pt-2 border-t border-ink-12/60">
              <Skeleton className="h-8 w-32 rounded-pill" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// keep import alive
void Film;
